import React from 'react'
import { useCallback, useState, useRef, useEffect, useMemo, RefObject } from 'react'
import './Machines.module.css'
import { Services } from '@/services/services';
import { Translates } from '@/services/repository/i18n';
import { Controller, SubmitHandler, useForm, UseFormReturn } from 'react-hook-form';
import { Machine, MachineType } from '@/services/model/machine';
import { ControlledDropdown } from '@/components/forms/common/CustomDropdown';


export interface FormData extends Machine { }

export interface FormError {
  message: string
}

export type FormErrors<T> = {
  [K in keyof T]: FormError;
}

export const MachineForm = ({ services, onSubmit, serverErrors, form }: { services: Services, onSubmit: (data: FormData, resetForm: () => void) => Promise<void>, serverErrors: FormErrors<FormData> | undefined, form: UseFormReturn<FormData> }) => {
  const [translates, setTranslates] = useState<Translates>()
  const [types, setTypes] = useState<MachineType[]>()
  const [imeiOptions, setIMEIOptions] = useState<string[]>([])
  const {
    setFocus,
    register,
    setError,
    handleSubmit,
    reset,
    getValues,
    control,
    formState: { errors }
  } = form

  useEffect(() => {
    services.i18n.get('main').then(setTranslates)
  }, [])

  useEffect(() => {
    services.machines.imeis().then(setIMEIOptions)
  }, [])

  useEffect(() => {
    services.machines.types().then(setTypes)
  }, [])

  useEffect(() => {
    if (serverErrors === undefined) {
      return
    }
    for (let i in serverErrors) {
      const key = i as keyof FormData
      setError(key, {
        type: 'server',
        message: translates?.for(serverErrors[key]!.message)
      })
      setFocus(key)
    }
  }, [translates, serverErrors])

  const handleFormSubmit: SubmitHandler<FormData> = (data) => {
    onSubmit(data, reset)
  };

  if (translates === undefined) {
    return (<div className='spinner'></div>)
  }

  return (<form onSubmit={handleSubmit(handleFormSubmit)}>
    {/* Name Field */}
    <div className='form-field'>
      <label htmlFor='name'>{translates.for('forms.machine.fields.name.title')}:</label>
      <input
        type='text'
        id='name'
        placeholder={translates.for('forms.machine.fields.name.placeholder')}
        {...register('name', {
          required: translates.for('forms.machine.errors.fields.name.required'),
          pattern: {
            value: /^[\p{L}0-9 .\-]*$/u,
            message: translates.for('forms.machine.errors.fields.name.pattern'),
          },
        })}
      />
      {errors.name && <p className='errorMessage'>{errors.name.message}</p>}
    </div>

    {/* Company ID Field */}
    {/* <div className='form-field'>
      <label htmlFor='companyId'>{translates.for('forms.machine.fields.companyId.title')}:</label>
      <input
        type='number'
        id='companyId'
        placeholder={translates.for('forms.machine.fields.companyId.placeholder')}
        {...register('companyId', {
          required: translates.for('forms.machine.errors.fields.companyId.required'),
          min: {
            value: 1,
            message: translates.for('forms.machine.errors.fields.companyId.min')
          }
        })}
      />
      {errors.companyId && <p className='errorMessage'>{errors.companyId.message}</p>}
    </div> */}

    {/* IMEI Field */}
    <div className='form-field'>
      <label htmlFor="imei">{translates.for('forms.machine.fields.imei.title')}:</label>
      {/* <div className='custom-select'>
        <select
          id="imei"
          {...register("imei", {
            // required: translates.for('forms.machine.errors.fields.imei.required'),
            // validate: (value) => value !== "" || translates.for('forms.machine.errors.fields.imei.required'),
          })}
        >
          {/* Default null value {translates.for('forms.machine.fields.imei.placeholder')} 
          <option value="">-</option>

          {/* IMEI options 
          {imeiOptions.map((imei) => (
            <option key={imei} value={imei} selected={getValues().imei === imei}>{imei}</option>
          ))}
        </select> */}

        <ControlledDropdown
          name='imei'
          control={form.control}
          options={imeiOptions.map((imei) => ({
            value: imei,
            label: imei
          })) || []}
          // rules={{
          //   required: translates.for(`forms.machine.errors.fields.imei.required`),
          // }}
        />
      {/* </div> */}
    </div>

    {/* Serial Number Field */}
    <div className='form-field'>
      <label htmlFor='serialNumber'>{translates.for('forms.machine.fields.serialNumber.title')}:</label>
      <input
        type='text'
        id='serialNumber'
        placeholder={translates.for('forms.machine.fields.serialNumber.placeholder')}
        {...register('serialNumber', {
          required: translates.for('forms.machine.errors.fields.serialNumber.required'),
        })}
      />
      {errors.serialNumber && <p className='errorMessage'>{errors.serialNumber.message}</p>}
    </div>

    {/* Production Year Field */}
    <div className='form-field'>
      <label htmlFor='manufactureDate'>{translates.for('forms.machine.fields.manufactureDate.title')}:</label>
      <input
        type='number'
        id='manufactureDate'
        placeholder={translates.for('forms.machine.fields.manufactureDate.placeholder')}
        {...register('manufactureDate', {
          required: translates.for('forms.machine.errors.fields.manufactureDate.required'),
          valueAsNumber: true,
          min: {
            value: 1900,
            message: translates.for('forms.machine.errors.fields.manufactureDate.min')
          },
          max: {
            value: new Date().getFullYear(),
            message: translates.for('forms.machine.errors.fields.manufactureDate.max')
          }
        })}
      />
      {errors.manufactureDate && <p className='errorMessage'>{errors.manufactureDate.message}</p>}
    </div>

    {/* Machine Type Field */}
    <div className='form-field'>
      <label htmlFor='machineTypeId'>{translates.for('forms.machine.fields.machineTypeId.title')}:</label>
      {/* <input
        type='text'
        id='machineType'
        placeholder={translates.for('forms.machine.fields.machineType.placeholder')}
        {...register('machineType')}
      /> */}
      <div className='custom-select'>
        {/* <select
          id="machineTypeId"
          {...register("machineTypeId", {
            // required: translates.for('forms.machine.errors.fields.imei.required'),
            // validate: (value) => value !== "" || translates.for('forms.machine.errors.fields.imei.required'),
          })} >
          {/* Default null value {translates.for('forms.machine.fields.imei.placeholder')} 
          <option value="" disabled>-</option>

          {/* IMEI options 
          {types?.map((machineType) => (
            <option key={machineType.id} value={machineType.id} selected={getValues().machineTypeId === machineType.id}>
              {machineType.manufacturer} - {machineType.category} - {machineType.model}
            </option>
          ))}
        </select> */}

        <CustomDropdown
          name='machineTypeId'
          control={form.control}
          options={types?.map((machineType) => ({
            value: machineType.id,
            label: `${machineType.manufacturer} - ${machineType.category} - ${machineType.model}`
          })) || []}
        />
      </div>

      {errors.machineTypeId && <p className='errorMessage'>{errors.machineTypeId.message}</p>}
    </div>

    {/* UDT Review Date Field */}
    <div className='form-field'>
      <label htmlFor='udtReviewDate'>{translates.for('forms.machine.fields.udtReviewDate.title')}:</label>
      <input
        type='date'
        id='udtReviewDate'
        {...register('udtReviewDate')}
      />
    </div>

    {/* Next UDT Inspection Date Field */}
    <div className='form-field'>
      <label htmlFor='nextUdtInspectionDate'>{translates.for('forms.machine.fields.nextUdtInspectionDate.title')}:</label>
      <input
        type='date'
        id='nextUdtInspectionDate'
        {...register('nextUdtInspectionDate')}
      />
    </div>

    {/* Warranty Expiration Date Field */}
    <div className='form-field'>
      <label htmlFor='warrantyExpirationDate'>{translates.for('forms.machine.fields.warrantyExpirationDate.title')}:</label>
      <input
        type='date'
        id='warrantyExpirationDate'
        {...register('warrantyExpirationDate')}
      />
    </div>

    <button className='submit-button' type='submit'>{translates.for('forms.machine.submit')}</button>
  </form>)
}
